import {
  ContainerLevelSimulationScenario,
  ContainerLevelSimulationStatus,
  ContainerLevelSimulationUpdate,
} from "@ots-frontend/ots-data";
import {BeakerIcon} from "@heroicons/react/20/solid";
import {
  Description,
  Dialog,
  DialogTitle,
  Field,
  Label,
  Select,
  Switch,
  SwitchField,
} from "catalyst";
import {CSSProperties, useEffect, useState} from "react";
import {OtsForm, OtsFormActions} from "../components/OtsForm";
import {OtsInlineAlert} from "../components/OtsInlineAlert";
import {OtsBrand, OtsColorSchemePreference, OtsThemeRoot} from "../theme/OtsTheme";

const scenarioDetails: Record<ContainerLevelSimulationScenario, {label: string; description: string}> = {
  NORMAL: {
    label: "Normal (mixed)",
    description: "Assigns each container a stable, random-looking behavior from the scenarios below.",
  },
  FILL_DRAIN: {
    label: "Fill and drain",
    description: "Cycles all containers through realistic filling and draining patterns.",
  },
  NEAR_CAPACITY: {
    label: "Near capacity",
    description: "Keeps all containers close to their configured capacity.",
  },
  POTENTIAL_SHUT_IN: {
    label: "Potential shut-in",
    description: "Raises container levels into the range used to exercise shut-in warnings.",
  },
  STALE_SENSOR: {
    label: "Stale sensor",
    description: "Produces old, unchanged readings so stale-data states can be tested.",
  },
  NOISY_SENSOR: {
    label: "Noisy sensor",
    description: "Adds rapid variation to exercise charts and noisy-reading handling.",
  },
};

export type ContainerLevelSimulationDialogProps = {
  open: boolean;
  status: ContainerLevelSimulationStatus;
  onSave: (update: ContainerLevelSimulationUpdate) => void | Promise<void>;
  onClose: () => void;
  brand?: OtsBrand;
  colorScheme?: OtsColorSchemePreference;
};

/** Controls the environment-gated, non-persistent container level feed. */
export const ContainerLevelSimulationDialog = ({
  open,
  status,
  onSave,
  onClose,
  brand,
  colorScheme,
}: ContainerLevelSimulationDialogProps) => {
  const [enabled, setEnabled] = useState(status.enabled);
  const [scenario, setScenario] = useState(status.scenario);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    if (open) {
      setEnabled(status.enabled);
      setScenario(status.scenario);
      setSaving(false);
      setError(undefined);
    }
  }, [open, status.enabled, status.scenario]);

  const submit = async () => {
    setSaving(true);
    setError(undefined);

    try {
      await onSave({enabled, scenario});
      onClose();
    } catch {
      setError("The simulation setting could not be saved. Check that the backend is still running and try again.");
      setSaving(false);
    }
  };

  const scenarios = status.scenarios.length ? status.scenarios : [status.scenario];
  const selectedDetails = scenarioDetails[scenario];

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!saving) onClose();
      }}
      size="lg"
      className="!bg-transparent !p-0">
      <OtsThemeRoot
        brand={brand}
        colorScheme={colorScheme}
        defaultColorScheme="system"
        className="rounded-[inherit] bg-[var(--ots-surface)] p-6 sm:p-8">
        <OtsForm
          aria-label="Simulated Container Levels"
          onSubmit={() => void submit()}
          actions={<div className="mt-3">
            <OtsFormActions submitLabel="Save" submittingLabel="Saving…" submitting={saving} onCancel={onClose}/>
          </div>}>
          <div className="flex items-start gap-4">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-[var(--ots-primary-container)] text-[var(--ots-primary)]">
              <BeakerIcon aria-hidden="true" className="size-5"/>
            </span>
            <div className="min-w-0 pt-0.5">
              <DialogTitle className="font-[family-name:var(--ots-font-display)] !text-lg !font-semibold !text-[var(--ots-secondary)]">
                Simulated Container Levels
              </DialogTitle>
              <p className="mt-1 text-sm text-[var(--ots-text-muted)]">
                Preview live boards with generated level history without writing to the database.
              </p>
            </div>
          </div>

          <div className="mt-2 space-y-6">
            <SwitchField>
              <Label>Enable simulated feed</Label>
              <Description>
                When off, container status and history return to their normal data sources immediately.
              </Description>
              <Switch
                color="dark/zinc"
                checked={enabled}
                onChange={setEnabled}
                disabled={saving}
                style={{
                  "--switch-bg": "var(--ots-primary)",
                  "--switch-bg-ring": "var(--ots-primary)",
                  "--switch-ring": "var(--ots-primary)",
                  "--switch-shadow": "color-mix(in srgb, var(--ots-primary) 20%, transparent)",
                  outlineColor: "var(--ots-primary)",
                } as CSSProperties}
                aria-label="Enable simulated feed"/>
            </SwitchField>

            <Field disabled={!enabled || saving}>
              <Label>Scenario</Label>
              <Description>Choose one behavior for every container, or let Normal mix them by container.</Description>
              <Select
                value={scenario}
                onChange={(event) => setScenario(event.target.value as ContainerLevelSimulationScenario)}
                disabled={!enabled || saving}
                aria-label="Scenario">
                {scenarios.map((value) => (
                  <option key={value} value={value}>{scenarioDetails[value]?.label ?? value}</option>
                ))}
              </Select>
              <p className="mt-2 text-xs leading-5 text-[var(--ots-text-muted)]">{selectedDetails?.description}</p>
            </Field>

            <div className="rounded-lg border border-[var(--ots-outline-variant)] bg-[var(--ots-background)] px-4 py-3 text-sm text-[var(--ots-text-muted)]">
              Generates a rolling {status.historyHours}-hour history every {status.sampleIntervalMinutes} minutes.
              Levels respect each container&apos;s dimensions and capacity, including small secondary oil or water levels.
              The setting and generated readings stay in memory and reset when the backend restarts.
            </div>

            {error && <OtsInlineAlert tone="danger">{error}</OtsInlineAlert>}
          </div>

        </OtsForm>
      </OtsThemeRoot>
    </Dialog>
  );
};
